import { GraphQLResult } from "@aws-amplify/api";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";

import { getMyActivities } from "@/graphql/queries";
import useAuthStore from "@/store/useAuthStore";
import { PAGE_URL } from "@/utils/constants";
import { formatAWSDateTime } from "@/utils/dateUtils";
import useMyActivityStore from "./useMyActivitiesStore";

// Convert activity data to standardized format
export const convertActivityData = (activitiesData: any, itemId: string) => {
  if (!activitiesData || !itemId) return null;

  // Try to find activity in each collection
  const findInCollection = (collection: any[], field = "id") => {
    return collection?.find((item) => item?.[field] === itemId);
  };

  // Check each activity type
  const activityTypes = [
    { type: "activities", field: "id" },
    { type: "circleRecommendations", field: "notifId" },
    { type: "eventRecommendations", field: "notifId" },
    { type: "connectionRecommendations", field: "notifId" },
    { type: "skillRecommendations", field: "notificationId" },
    { type: "endorsements", field: "id" },
    { type: "activeEndorsements", field: "id" },
    { type: "circleInvitations", field: "id" },
    { type: "eventInvitations", field: "id" },
    { type: "championRequests", field: "id" },
    { type: "championInvitations", field: "id" },
    { type: "mentorshipRequests", field: "id" },
    { type: "connectionActivities", field: "notifId" },
    { type: "skillVerificationRequests", field: "id" },
    { type: "relationshipUpgradeRequests", field: "notifId" },
  ];

  // Find the activity in the correct collection
  for (const { type, field } of activityTypes) {
    const found = findInCollection(activitiesData[type], field);
    if (found) {
      return { ...found, APANIH: type }; // APANIH is a marker to identify the activity type
    }
  }

  return null;
};

// Define the refetch options type
interface RefetchOptions {
  silent?: boolean; // Don't show loading state if true
  merge?: boolean; // Merge with existing data if true, replace if false
  onlyNewer?: boolean; // Only fetch activities newer than the most recent one
  forceRefresh?: boolean; // Force a full refresh even if throttled
  timeWindow?: {
    // Custom time window for fetching
    startTime: dayjs.Dayjs;
    endTime: dayjs.Dayjs;
  };
}

// Hook to fetch activities with enhanced refetch capabilities
export const useGetMyActivity = (
  initialFetch = false,
  autoRefreshInterval = 0
) => {
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const { activities, setActivities, updateActivities } = useMyActivityStore();
  const lastFetchedRef = useRef<dayjs.Dayjs | null>(null);
  const refetchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fetchCountRef = useRef<number>(0);
  const throttleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handlerLogout = useAuthStore.useLogout();
  const { signOut } = useAuthenticator((context) => [context.signOut]);
  const router = useRouter();

  const logout = (): void => {
    handlerLogout();
    signOut();
    router.push({ pathname: PAGE_URL.login });
  };

  // Get the most recent activity date
  const getMostRecentActivityDate = useCallback(() => {
    if (!activities?.orderedItems?.length) return null;

    // Find the most recent activity
    const dates = activities.orderedItems
      .map((item) => item?.createdAt)
      .filter(Boolean)
      .map((date) => dayjs(date));

    return dates.length
      ? dates.reduce((max, date) => (date.isAfter(max) ? date : max), dates[0])
      : null;
  }, [activities]);

  // Merge new activities with existing ones
  const mergeActivities = useCallback((existingData: any, newData: any) => {
    if (!existingData) return newData;
    if (!newData) return existingData;

    // Helper to merge arrays with deduplication
    const mergeArrays = (
      existing: any[] = [],
      incoming: any[] = [],
      idField = "id"
    ) => {
      const existingMap = new Map(
        existing.map((item) => [item[idField], item])
      );

      // Update or add incoming items
      incoming.forEach((item) => {
        if (item[idField]) {
          existingMap.set(item[idField], item);
        }
      });

      return Array.from(existingMap.values());
    };

    // Merge ordered items
    const orderedItems = mergeArrays(
      existingData.orderedItems,
      newData.orderedItems,
      "id"
    ).sort((a, b) => {
      return dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();
    });

    // Create the merged result
    const result = { ...existingData };
    result.orderedItems = orderedItems;

    // Merge each activity type collection
    const activityTypes = [
      "activities",
      "circleRecommendations",
      "eventRecommendations",
      "connectionRecommendations",
      "skillRecommendations",
      "endorsements",
      "activeEndorsements",
      "circleInvitations",
      "eventInvitations",
      "championRequests",
      "championInvitations",
      "mentorshipRequests",
      "connectionActivities",
      "skillVerificationRequests",
      "relationshipUpgradeRequests",
    ];

    activityTypes.forEach((type) => {
      result[type] = mergeArrays(existingData[type], newData[type]);
    });

    return result;
  }, []);

  // Debounced function to prevent multiple rapid fetches
  // Fix the debouncedFetch dependencies (around line 250)
  const debouncedFetch = useCallback(
    debounce(async (options: RefetchOptions = {}) => {
      if (isFetching) return null;

      try {
        if (!options.silent) {
          setIsFetching(true);
        }

        // Determine time window for fetching
        let startTime, endTime;

        if (options.timeWindow) {
          // Use custom time window if provided
          startTime = options.timeWindow.startTime;
          endTime = options.timeWindow.endTime;
        } else if (options.onlyNewer) {
          // Fetch only newer activities than the most recent one
          const mostRecentDate = getMostRecentActivityDate();
          if (mostRecentDate) {
            startTime = mostRecentDate;
            endTime = dayjs();
          } else {
            // Fall back to default if no recent activity
            endTime = dayjs();
            // startTime = getDateWeeksAgo(2, endTime);
            startTime = dayjs().subtract(2, "year");
          }
        } else {
          // Default: last 2 weeks
          endTime = dayjs();
          // startTime = getDateWeeksAgo(2, endTime);
          startTime = dayjs().subtract(2, "year");
        }

        let response;
        try {
          // Format dates for API
          response = await API.graphql(
            graphqlOperation(getMyActivities, {
              startTime: formatAWSDateTime(startTime),
              endTime: null,
            })
          );
        } catch (error: any) {
          // add object checking
          if (
            error?.errors?.[0] &&
            (error?.errors?.[0]?.message === "Current User Has Been Deleted" ||
              error?.errors?.[0]?.errorType === "Unauthorized")
          ) {
            logout();
            return;
          }
        }

        const newActivitiesData = (response as GraphQLResult<any>)?.data?.getMyActivities;

        if (newActivitiesData) {
          if (options.merge && activities) {
            // Merge with existing data
            const mergedData = mergeActivities(activities, newActivitiesData);
            setActivities(mergedData);
          } else {
            // Replace existing data
            setActivities(newActivitiesData);
          }
        }

        // Update last fetched time
        lastFetchedRef.current = dayjs();
        fetchCountRef.current += 1;

        return newActivitiesData;
      } catch (err) {
        console.error("Error fetching activities:", err);
        setError(
          err instanceof Error ? err : new Error("Unknown error occurred")
        );
        return null;
      } finally {
        if (!options.silent) {
          setIsFetching(false);
        }
      }
    }, 300),
    // Lines 250-260 - This dependency array causes re-creation on every render
    [
      activities,
      isFetching,
      getMostRecentActivityDate,
      mergeActivities,
      setActivities,
    ]
  );

  // Main refetch function with throttling protection
  const fetchMyActivities = useCallback(
    async (options: RefetchOptions = {}) => {
      // Clear any existing throttle timeout
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = null;
      }

      // If force refresh is not requested and we've fetched recently, throttle
      if (
        !options.forceRefresh &&
        lastFetchedRef.current &&
        dayjs().diff(lastFetchedRef.current, "second") < 10
      ) {
        // console.log('Throttling refetch - requested too soon');

        // Set a timeout to fetch after the throttle period
        throttleTimeoutRef.current = setTimeout(
          () => {
            debouncedFetch(options);
          },
          10000 - dayjs().diff(lastFetchedRef.current, "millisecond")
        );

        return null;
      }

      return debouncedFetch(options);
    },
    [debouncedFetch]
  );

  useEffect(() => {
    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
      } catch (err) {
        logout();
      }
    })();
  }, []);

  // Setup auto-refresh interval if specified
  useEffect(() => {
    if (autoRefreshInterval > 0) {
      refetchTimerRef.current = setInterval(() => {
        // Auto-refresh should be silent and only fetch newer items
        fetchMyActivities({
          silent: true,
          merge: true,
          onlyNewer: true,
        });
      }, autoRefreshInterval);
    }

    return () => {
      if (refetchTimerRef.current) {
        clearInterval(refetchTimerRef.current);
      }
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, [autoRefreshInterval, fetchMyActivities]);

  // Initial fetch on mount if requested
  useEffect(() => {
    if (initialFetch) {
      fetchMyActivities();
    }
  }, [initialFetch, fetchMyActivities]);

  return {
    refetch: fetchMyActivities,
    isFetching,
    error,
    lastFetched: lastFetchedRef.current,
    fetchCount: fetchCountRef.current,
  };
};

// Enhanced Zustand store to support the refetch algorithm
export const enhanceMyActivityStore = () => {
  // Get the existing store
  const existingStore = useMyActivityStore();

  // Add the updateActivities method if it doesn't exist
  if (!existingStore.updateActivities) {
    // This is just a suggestion - you'll need to modify your actual Zustand store
    console.warn("Please add updateActivities method to your Zustand store");
  }

  return existingStore;
};

export default useGetMyActivity;
